import type { FC, JSX } from 'react';

import { useCopyPropsContext } from '../../context/copy-props';
import { useGetMediaUrl } from '../../hooks/get-media-url';
import { useImage } from '../../hooks/image';
import { useLoadViewControl } from '../../hooks/load-view-control';
import RightPanelStatusComponent, { type IRightPanelStatusComponentProps } from '../../right-panel/response/RightPanel.Response';
import ImageComponent from '../image';
import { TypeButton } from './button';
import type { ICardItem, IDropDownItem, IRTI } from './hooks';
import { RefreshComponent } from './Refresh';
import { AlertSVG, CheckSVG, CopySVG, ElectionSVG, PreviewSVG, PulseSVG } from './svgs';
import ToolTip from './tool-tip';

interface IHVC extends React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
	view: boolean;
	children?: React.ReactNode;
	removeDOM?: boolean;
	load?: boolean;
	auth?: boolean;
	altComponent?: JSX.Element;
}

export const Hvc: FC<IHVC> = ({
	view,
	children,
	className = '',
	removeDOM = false,
	load = false,
	auth = false,
	altComponent,
	...props
}) => {
	if (load) return <PulseSVG />;

	if (removeDOM) {
		if (view) {
			return (
				<div
					className={className}
					{...props}
				>
					{children}
				</div>
			);
		}
		if (auth) {
			return <AuthorizedPage />;
		}
		return !view && altComponent ? altComponent : null;
	}

	return (
		<>
			<div
				className={`${!view ? 'd-none' : ''} ${className}`}
				{...props}
			>
				{children}
			</div>
			{!view && altComponent ? altComponent : null}
		</>
	);
};

interface IHVCLoad extends React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
	view?: boolean;
	children?: any;
	removeDOM?: boolean;
	load?: boolean;
	loadTxt?: string;
	onRefresh?: () => void;
	errorMessage?: string;
	response?: IRightPanelStatusComponentProps;
}
export const HvcLoad: FC<IHVCLoad> = ({
	view = true,
	children,
	className = '',
	removeDOM = false,
	load = false,
	loadTxt,
	onRefresh,
	errorMessage,
	response,
	...props
}) => {
	const { shouldRemoveDOM, shouldHideWithClass } = useLoadViewControl({
		load,
		view,
		removeDOM,
	});

	if (errorMessage)
		return (
			<div className='f-column-23 align-items-center justify-content-center text-center'>
				<h3 className='m-0'>Error</h3>
				<p>{errorMessage}</p>
			</div>
		);

	if (load) {
		return (
			<div className='f-row-10 aic hw-mx'>
				<PulseSVG data-testid='spinner' />
				{loadTxt && <p className='m-0 text-little'>{loadTxt}</p>}
			</div>
		);
	}

	if (response?.view) {
		return <RightPanelStatusComponent {...response} />;
	}

	if (shouldRemoveDOM) return null;

	return (
		<>
			{onRefresh && (
				<div className='pb-2 f-row justify-content-end'>
					<RefreshComponent
						onRefresh={onRefresh}
						text={load ? 'Fetching data...' : 'Refresh'}
					/>
				</div>
			)}
			<div
				className={`${shouldHideWithClass ? 'd-none' : ''} ${className}`}
				{...props}
			>
				{children}
			</div>
		</>
	);
};

const AuthorizedPage = () => (
	<div className='f-row-10 align-items-center'>
		<AlertSVG />
		<p className='m-0 text-danger'>You are not authorized to view this page</p>
	</div>
);

export const OverViewHeader = ({
	title,
	moreInfo,
	onMoreInfo,
	isInfo,
	copy,
	onCopy,
}: {
	title: string;
	moreInfo?: boolean;
	isInfo?: boolean;
	onMoreInfo?: () => void;
	onCopy?: () => void;
	copy?: boolean;
}) => (
	<p
		className='font-10 mb-2 color-label'
		style={{ textTransform: 'uppercase', letterSpacing: '0.1rem' }}
	>
		<strong>{title}</strong>
		{moreInfo ? (
			<span onClick={onMoreInfo}>
				<i className={`fas fa-${isInfo ? 'minus' : 'info'}-circle ml-2`} />
			</span>
		) : null}
		&nbsp;&nbsp;&nbsp;
		{copy ? (
			<span
				className='font-9 cursor-pointer'
				onClick={() => onCopy?.()}
			>
				Copy
			</span>
		) : null}
	</p>
);

export const CardItems = ({ title, value, onHandleClick, copy, mini, description, ellipsis, renderValue }: ICardItem) => {
	const copyProps = useCopyPropsContext();

	const onCopy = () => {
		copyProps?.copy(value as string);
	};

	const miniTxt = mini ? 'tiny' : 'medium';

	const isClick = typeof onHandleClick === 'function';
	const hasCustomRender = renderValue !== undefined && renderValue !== null;

	const ellipsisStyle = ellipsis
		? {
				overflow: 'hidden',
				textOverflow: 'ellipsis',
				whiteSpace: 'nowrap',
			}
		: {};

	return (
		<div className='bet-chnnel-item-wrapper'>
			<OverViewHeader
				title={title}
				copy={copy && !hasCustomRender}
				onCopy={onCopy}
			/>
			<div
				className='f-row-7 align-items-center item-value gap-7'
				onClick={() => {
					onHandleClick?.();
				}}
			>
				{description && <ToolTip tip={description} />}
				{hasCustomRender ? (
					renderValue
				) : (
					<p
						className={`text-${miniTxt} cursor-pointer m-0`}
						style={{
							textDecoration: 'none',
							color: 'inherit',
							wordWrap: 'break-word',
							width: 'max-content',
							...ellipsisStyle,
						}}
					>
						{value}
					</p>
				)}
				{isClick ? <PreviewSVG color='var(--text-muted, #a1a1a1)' /> : null}
				{copy && !hasCustomRender ? (
					<div
						className='hw-max cursor-pointer'
						onClick={onCopy}
					>
						<CopySVG />
					</div>
				) : null}
			</div>
		</div>
	);
};

export const CopyComponent = ({ children, value }: { children?: React.ReactNode; value: string }) => {
	const copyProps = useCopyPropsContext();

	const onCopy = () => {
		copyProps?.copy(value);
	};
	return (
		<div
			className='f-row-7 align-items-center cursor-pointer'
			onClick={onCopy}
		>
			{children}
			<div className='hw-max'>{copyProps?.copySuccess ? <CheckSVG color='#1B6B4A' /> : <CopySVG />}</div>
		</div>
	);
};

export const DropDownMenu = ({
	id,
	items,
	dropDownIcon,
	children,
}: {
	id: string;
	items: IDropDownItem[];
	dropDownIcon?: string | JSX.Element;
	children?: any;
}) => (
	<div className='dropdown'>
		<div
			className='dropdown-toggle cursor-pointer'
			id={id}
			data-toggle='dropdown'
			aria-haspopup='true'
			aria-expanded='false'
		>
			{children}
			{!children && (
				<>
					{typeof dropDownIcon === 'string' ? (
						<i className={`${dropDownIcon || 'fas fa-angle-down'} color-primary`} />
					) : (
						dropDownIcon
					)}
				</>
			)}
		</div>
		<ul
			className='dropdown-menu dm-menu'
			aria-labelledby={id}
		>
			{items.map((i, index) => (
				<li
					className='dropdown-item d-flex py-2 text-little'
					onClick={i.disabled ? undefined : i.action}
					key={index}
					style={{
						color: i.disabled ? '#a1a1a1' : '',
						cursor: i.disabled ? 'not-allowed' : 'pointer',
					}}
				>
					{i?.icon && (
						<span
							style={{ width: '25px' }}
							className='d-block'
						>
							<i className={i?.icon} />
						</span>
					)}
					{i.title}
				</li>
			))}
		</ul>
	</div>
);

export const SeparatorComponent = () => (
	<div style={{ width: '1px', height: '10px', background: 'var(--border-primary, #cacaca)' }} />
);

export const MediaItem = ({ url, isCover }: { url: string; isCover?: boolean }) => {
	const imgProps = useImage();
	const { mediaUrl } = useGetMediaUrl(url, imgProps);

	return (
		<div
			className='w-100 h-100 d-flex justify-content-center'
			style={{
				overflow: isCover ? 'auto' : '',
				alignItems: isCover ? 'start' : 'center',
			}}
		>
			{mediaUrl.load ? (
				<PulseSVG />
			) : mediaUrl.type === 'image' ? (
				<ImageComponent
					src={mediaUrl.url}
					alt=''
					style={{
						objectFit: isCover ? 'cover' : 'contain',
						height: isCover ? 'auto' : '100%',
					}}
					className='w-100'
				/>
			) : mediaUrl.type === 'doc' ? (
				<iframe
					src={mediaUrl.url.replace('?dl=0', '?raw=1')}
					width='100%'
					height='100%'
					title='firefighter'
					// sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
				/>
			) : null}
		</div>
	);
};

export const SelectItem = ({ title, description, isActive, onProceed, onSelect, disabled }: IRTI) => (
	<div
		className={`rounded p-4 f-row-10 align-items-start result-item-type ${isActive ? 'active' : ''}`}
		onClick={!isActive ? onSelect : undefined}
		data-testid={title.toLowerCase() + '-result'}
	>
		<div className='f-row-10 align-items-center'>
			<ElectionSVG />
		</div>
		<div className='f-column-15'>
			<h6 className={`m-0 ${disabled ? 'color-label' : ''}`}>{title}</h6>
			<p className={`m-0 text-little ${disabled ? 'color-label' : ''}`}>{description}</p>
			<Hvc
				removeDOM
				view={isActive || false}
				className='mt-3'
			>
				<TypeButton
					title='Proceed'
					buttonSize='small'
					onClick={onProceed}
					disabled={disabled}
					buttonType={disabled ? 'disabled' : 'bold'}
				/>
			</Hvc>
		</div>
	</div>
);
